import { useIDBKeyval } from "@vueuse/integrations/useIDBKeyval";
import { version } from "../../package.json";
import type { OnboardingResponse } from "@/types/onboarding";
import type { TaskOptionsInitResponse } from "@/types/init";
import type { AssistantAccountSummaryData } from "@/types/assistant";
import type { AccountConfiguration, UserAccountDetails } from "@/types/account";
import type { QueryKey } from "@tanstack/vue-query";
import type { TenantAccount } from "@/types/tenant";
import type { JobNotificationData } from "@/types/jobs";

interface CachedData {
  appVersion: Record<string, string>;
  initData: Record<string, TaskOptionsInitResponse | null>;
  onboardingData: Record<string, OnboardingResponse | null>;
  accountSummary: Record<string, AssistantAccountSummaryData | null>;
  account: Record<string, UserAccountDetails | null>;
  accountConfiguration: Record<string, AccountConfiguration | null>;
  tenantAccountList: Record<string, TenantAccount[] | null>;
  jobNotifications: Record<string, JobNotificationData[] | null>;
}

type CachedDataResponseMap = {
  appVersion: CachedData["appVersion"][string];
  initData: CachedData["initData"][string];
  onboardingData: CachedData["onboardingData"][string];
  accountSummary: CachedData["accountSummary"][string];
  account: CachedData["account"][string];
  accountConfiguration: CachedData["accountConfiguration"][string];
  tenantAccountList: CachedData["tenantAccountList"][string];
  jobNotifications: CachedData["jobNotifications"][string];
  // add more types as needed
};

type CachedDataKey = keyof CachedData;

function apiCacheHelper() {
  const appVersion = version;
  const queryClient = useQueryClient();
  const initializedQueries: Record<CachedDataKey, boolean> = {
    appVersion: false,
    initData: false,
    onboardingData: false,
    accountSummary: false,
    account: false,
    accountConfiguration: false,
    tenantAccountList: false,
    jobNotifications: false,
  };
  const defaultData: CachedData = {
    initData: {},
    onboardingData: {},
    accountSummary: {},
    appVersion: {},
    account: {},
    accountConfiguration: {},
    tenantAccountList: {},
    jobNotifications: {},
  };

  const { data, isFinished, set } = useIDBKeyval("api-cache", defaultData);

  const getCachedData = <T extends CachedDataKey>(
    key: T,
    scopedToAccount = true
  ): CachedDataResponseMap[T] | null => {
    try {
      if (import.meta.env.DEV)
        console.info(
          `Getting cached data for key: ${key}, scopedToAccount: ${scopedToAccount}`
        );
      const accountId = currentSessionInformation.appAccountId;
      const storedAppVersion = data.value.appVersion?.[accountId];
      if (!isFinished.value) return null;
      if (storedAppVersion && storedAppVersion !== appVersion) {
        resetAccountData();
        return null;
      }

      const response = data.value[key];
      if (!response) return null;
      if (typeof response === "string")
        return (response as unknown as CachedDataResponseMap[T]) ?? null;

      if (!scopedToAccount) {
        return response as unknown as CachedDataResponseMap[T];
      }
      if (!accountId) return null;
      const res = response?.[accountId] as unknown as CachedDataResponseMap[T];
      return Object.keys(res || {}).length > 0 ? res : null;
    } catch (e) {
      console.info("Error getting cached data:", e);
      return null;
    }
  };

  const setCachedData = <T extends CachedDataKey>(
    key: T,
    value: CachedDataResponseMap[T],
    scopedToAccount = true
  ): void => {
    try {
      if (!isFinished.value) return;
      const accountId = currentSessionInformation.appAccountId;
      const storedAppVersion = data.value.appVersion?.[accountId];
      if (import.meta.env.DEV)
        console.info(
          `Setting cached data for key: ${key}, scopedToAccount: ${scopedToAccount}`
        );

      if (scopedToAccount && !accountId) return;
      if (storedAppVersion && storedAppVersion !== appVersion) resetAccountData();

      if (!storedAppVersion) {
        data.value.appVersion = {
          ...data.value.appVersion,
          [accountId]: appVersion,
        };
      }

      if (scopedToAccount) {
        data.value[key][accountId] = value;
      } else {
        data.value[key] = value as unknown as CachedData[T];
      }
    } catch (_e) {
      console.error(
        `Error setting cached data for key: ${key}, scopedToAccount: ${scopedToAccount}`
      );
    }
  };

  function resetData() {
    console.info("Resetting API cache data.");
    set(defaultData);
  }

  function resetAccountData() {
    console.info("Resetting account-specific API cache data.");
    const accountId = currentSessionInformation.appAccountId;
    if (!accountId) {
      console.warn("No active account ID set, cannot reset account data.");
      return;
    }
    data.value.initData[accountId] = null;
    data.value.onboardingData[accountId] = null;
    data.value.accountSummary[accountId] = null;
    data.value.appVersion[accountId] = appVersion;
    data.value.account[accountId] = null;
    data.value.accountConfiguration[accountId] = null;
    data.value.tenantAccountList[accountId] = null;
    data.value.jobNotifications[accountId] = null;
  }

  function maybeGetFromCacheAndRefetch<T extends CachedDataKey>(
    key: T,
    queryKey: QueryKey,
    scopedToAccount = true
  ): { cachedData: CachedDataResponseMap[T] | null; isInitialized: boolean } {
    const cachedData = getCachedData(key, scopedToAccount);
    const isInitialized = initializedQueries[key];
    initializedQueries[key] = true;
    if (cachedData) {
      if (!isInitialized)
        setTimeout(
          () => queryClient.invalidateQueries({ queryKey, exact: true }),
          1.5 * SECOND
        );
      return { cachedData, isInitialized };
    }
    return { cachedData: null, isInitialized };
  }

  return {
    getCachedData,
    setCachedData,
    resetData,
    maybeGetFromCacheAndRefetch,
    isFinished,
  };
}

export const useApiCache = createSharedComposable(apiCacheHelper);
